import { createContext, useContext, useState, useCallback, useEffect, type ReactNode, type Dispatch, type SetStateAction } from 'react';
import type { Hymn } from '@features/hymns/types';
import type { Playlist } from '@/types/supabase';
import { useAuth } from '@features/auth/context/AuthContext';
import {
  getOrCreateDefaultPlaylist,
  getPlaylistWithItems,
  addHymnToPlaylist,
  removeHymnFromPlaylist,
  clearPlaylist as clearPlaylistService,
  reorderPlaylistItems,
} from '@features/playlists/services/playlistsService';

export type RepeatMode = 'none' | 'all' | 'one';

interface PlaylistContextType {
  playlist: Hymn[];
  currentPlaylistId: string | null;
  playlistMetadata: Playlist | null;
  addToPlaylist: (hymn: Hymn) => Promise<void>;
  removeFromPlaylist: (hymnId: number) => Promise<void>;
  clearPlaylist: () => Promise<void>;
  isInPlaylist: (hymnId: number) => boolean;
  reorderPlaylist: (fromIndex: number, toIndex: number) => Promise<void>;
  currentIndex: number;
  setCurrentIndex: Dispatch<SetStateAction<number>>;
  isPlaying: boolean;
  setIsPlaying: (playing: boolean) => void;
  repeatMode: RepeatMode;
  setRepeatMode: (mode: RepeatMode) => void;
  playbackRate: number;
  setPlaybackRate: (rate: number) => void;
  shuffle: boolean;
  setShuffle: (shuffle: boolean) => void;
  shuffledIndices: number[];
  loading: boolean;
  refreshPlaylist: () => Promise<void>;
}

const PlaylistContext = createContext<PlaylistContextType | null>(null);

export function PlaylistProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [playlist, setPlaylist] = useState<Hymn[]>([]);
  const [currentPlaylistId, setCurrentPlaylistId] = useState<string | null>(null);
  const [playlistMetadata, setPlaylistMetadata] = useState<Playlist | null>(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [repeatMode, setRepeatMode] = useState<RepeatMode>('none');
  const [playbackRate, setPlaybackRate] = useState(1);
  const [shuffle, setShuffle] = useState(false);
  const [shuffledIndices, setShuffledIndices] = useState<number[]>([]);
  const [loading, setLoading] = useState(false);

  // Fisher-Yates shuffle algorithm
  const shuffleArray = useCallback((length: number, currentIdx: number): number[] => {
    const indices = Array.from({ length }, (_, i) => i);

    // Remove current index and shuffle the rest
    const current = indices.splice(currentIdx, 1)[0];

    // Shuffle remaining indices
    for (let i = indices.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [indices[i], indices[j]] = [indices[j], indices[i]];
    }

    // Put current index at the beginning
    return [current, ...indices];
  }, []);

  // Convert Supabase hymn to legacy format
  const convertHymn = useCallback((supabaseHymn: any): Hymn => {
    return {
      uniqueId: supabaseHymn.id,
      id: supabaseHymn.hymn_number,
      category: supabaseHymn.category,
      title: supabaseHymn.title,
      keywords: [],
      videos: supabaseHymn.youtube_url
        ? [{ churchName: '한국찬송가공회', youtubeUrl: supabaseHymn.youtube_url }]
        : [],
    };
  }, []);

  // Load user's default playlist
  const refreshPlaylist = useCallback(async () => {
    if (!user) {
      setPlaylist([]);
      setCurrentPlaylistId(null);
      setPlaylistMetadata(null);
      return;
    }

    setLoading(true);
    try {
      // Get or create default playlist
      const { data: defaultPlaylist } = await getOrCreateDefaultPlaylist(user.id);

      if (defaultPlaylist) {
        setCurrentPlaylistId(defaultPlaylist.id);
        setPlaylistMetadata(defaultPlaylist);

        // Load playlist items
        const { data: playlistData } = await getPlaylistWithItems(defaultPlaylist.id);

        if (playlistData?.playlist_items) {
          const hymns = playlistData.playlist_items
            .filter((item: any) => item.hymn)
            .map((item: any) => convertHymn(item.hymn));
          setPlaylist(hymns);
        }
      }
    } catch (error) {
      console.error('Error loading playlist:', error);
    } finally {
      setLoading(false);
    }
  }, [user, convertHymn]);

  // Load playlist on mount and when user changes
  useEffect(() => {
    refreshPlaylist();
  }, [refreshPlaylist]);

  const addToPlaylist = useCallback(async (hymn: Hymn) => {
    if (!currentPlaylistId) return;

    // Check if already in playlist
    if (playlist.some(h => h.id === hymn.id)) {
      return;
    }

    try {
      // Add to Supabase
      const { error } = await addHymnToPlaylist(currentPlaylistId, hymn.id);

      if (!error) {
        // Update local state
        setPlaylist(prev => [...prev, hymn]);
      }
    } catch (error) {
      console.error('Error adding to playlist:', error);
    }
  }, [currentPlaylistId, playlist]);

  const removeFromPlaylist = useCallback(async (hymnId: number) => {
    if (!currentPlaylistId) return;

    const index = playlist.findIndex(h => h.id === hymnId);
    if (index === -1) return;

    try {
      // Remove from Supabase
      const { error } = await removeHymnFromPlaylist(currentPlaylistId, hymnId);

      if (!error) {
        // Update local state
        const newList = playlist.filter(h => h.id !== hymnId);
        setPlaylist(newList);

        // Adjust current index
        if (index < currentIndex) {
          setCurrentIndex(curr => Math.max(0, curr - 1));
        } else if (index === currentIndex && currentIndex >= newList.length) {
          setCurrentIndex(Math.max(0, newList.length - 1));
        }
      }
    } catch (error) {
      console.error('Error removing from playlist:', error);
    }
  }, [currentPlaylistId, playlist, currentIndex]);

  const clearPlaylist = useCallback(async () => {
    if (!currentPlaylistId) return;

    try {
      const { error } = await clearPlaylistService(currentPlaylistId);

      if (!error) {
        setPlaylist([]);
        setCurrentIndex(0);
        setIsPlaying(false);
      }
    } catch (error) {
      console.error('Error clearing playlist:', error);
    }
  }, [currentPlaylistId]);

  const isInPlaylist = useCallback((hymnId: number) => {
    return playlist.some(h => h.id === hymnId);
  }, [playlist]);

  const reorderPlaylist = useCallback(async (fromIndex: number, toIndex: number) => {
    if (!currentPlaylistId) return;

    // Optimistic update
    const newList = [...playlist];
    const [removed] = newList.splice(fromIndex, 1);
    newList.splice(toIndex, 0, removed);
    setPlaylist(newList);

    // Adjust current index
    if (fromIndex === currentIndex) {
      setCurrentIndex(toIndex);
    } else if (fromIndex < currentIndex && toIndex >= currentIndex) {
      setCurrentIndex(curr => curr - 1);
    } else if (fromIndex > currentIndex && toIndex <= currentIndex) {
      setCurrentIndex(curr => curr + 1);
    }

    try {
      // Update in Supabase
      await reorderPlaylistItems(currentPlaylistId, fromIndex, toIndex);
    } catch (error) {
      console.error('Error reordering playlist:', error);
      // Revert on error
      await refreshPlaylist();
    }
  }, [currentPlaylistId, playlist, currentIndex, refreshPlaylist]);

  // Handle shuffle toggle
  const handleSetShuffle = useCallback((newShuffle: boolean) => {
    if (newShuffle && playlist.length > 0) {
      // Enable shuffle: create shuffled indices with current song first
      const shuffled = shuffleArray(playlist.length, currentIndex);
      setShuffledIndices(shuffled);
      setCurrentIndex(0); // Current song is now at index 0 in shuffled array
    } else {
      // Disable shuffle: return to original order
      if (shuffledIndices.length > 0) {
        const originalIndex = shuffledIndices[currentIndex];
        setCurrentIndex(originalIndex);
      }
      setShuffledIndices([]);
    }
    setShuffle(newShuffle);
  }, [playlist.length, currentIndex, shuffledIndices, shuffleArray]);

  return (
    <PlaylistContext.Provider
      value={{
        playlist,
        currentPlaylistId,
        playlistMetadata,
        addToPlaylist,
        removeFromPlaylist,
        clearPlaylist,
        isInPlaylist,
        reorderPlaylist,
        currentIndex,
        setCurrentIndex,
        isPlaying,
        setIsPlaying,
        repeatMode,
        setRepeatMode,
        playbackRate,
        setPlaybackRate,
        shuffle,
        setShuffle: handleSetShuffle,
        shuffledIndices,
        loading,
        refreshPlaylist,
      }}
    >
      {children}
    </PlaylistContext.Provider>
  );
}

export function usePlaylist() {
  const context = useContext(PlaylistContext);
  if (!context) {
    throw new Error('usePlaylist must be used within a PlaylistProvider');
  }
  return context;
}
